import { createEventAndRecurrentsDTO } from "../../models/dtos/createEventAndRecurrentsDTO";
import {
  Event,
  EventUsers,
  EventsParams,
  EventFetch,
  EventCancellationsResponse,
  EventMessage,
  EventInterestParams,
  EventInterestResponse,
  EventsPagination,
  PaginatedEventsResponse,
} from "../../models/events";
import {
  UpdateHostFetch,
  EventUsersIdsFetch,
  RemoveEventUsersFetch,
  RemoveEventUsersResult,
} from "../../models/forms";
import { api } from "./api";
import { mapEvent } from "../../utils/mappers";
import { isObjectRecord } from "../../utils/functions/api-errors";

type ApiRecord = Record<string, unknown>;
type EventApiRecord = ApiRecord;
type EventImage = NonNullable<Event["images"]>[number];
type EventImagePayload = Pick<EventImage, "url"> & Partial<Omit<EventImage, "url">>;

type ApiDataResponse<T> = {
  success?: boolean;
  data: T;
};

type CreateEventResponse = {
  id: number;
  event_type_id: number;
};

type CreateEventAndRecurrentsResponse = {
  success: string;
  createdGroupId: string;
  insertedEventIds: string[];
};

type EventCollectionContainer = {
  items?: unknown;
  events?: unknown;
};

const isEventApiRecordArray = (value: unknown): value is EventApiRecord[] =>
  Array.isArray(value) && value.every(isObjectRecord);

const hasApiData = <T>(response: ApiDataResponse<T> | T): response is ApiDataResponse<T> =>
  isObjectRecord(response) && "data" in response && response.data !== undefined;

const unwrapApiData = <T>(response: ApiDataResponse<T> | T): T => {
  return hasApiData(response) ? response.data : response;
};

const getApiPayload = (response: unknown): unknown => {
  if (isObjectRecord(response) && "data" in response && response.data !== undefined) {
    return response.data;
  }

  return response;
};

const getEventsFromContainer = ({
  items,
  events,
}: EventCollectionContainer): EventApiRecord[] => {
  if (isEventApiRecordArray(items)) return items;
  if (isEventApiRecordArray(events)) return events;
  return [];
};

const normalizeEventListResponse = (response: unknown): EventApiRecord[] => {
  if (isEventApiRecordArray(response)) return response;
  if (!isObjectRecord(response)) return [];

  const { data } = response;

  if (isEventApiRecordArray(data)) return data;
  if (isObjectRecord(data)) {
    return getEventsFromContainer(data);
  }

  return getEventsFromContainer(response);
};

const normalizeEventResponse = (response: unknown): EventApiRecord | undefined => {
  const payload = getApiPayload(response);

  if (isObjectRecord(payload)) return payload;
  if (Array.isArray(payload)) return payload.find(isObjectRecord);

  return undefined;
};

const getArrayPayload = <T>(
  response: unknown,
  isItem: (value: unknown) => value is T
): T[] => {
  const payload = getApiPayload(response);
  return Array.isArray(payload) ? payload.filter(isItem) : [];
};

const isEventUser = (value: unknown): value is EventUsers =>
  isObjectRecord(value) && typeof value.userId === "number";

const isEventImage = (value: unknown): value is EventImage =>
  isObjectRecord(value) &&
  typeof value.id === "number" &&
  typeof value.url === "string" &&
  typeof value.format === "string";

const isEventInterestUser = (value: unknown): value is EventInterestResponse["items"][number] =>
  isObjectRecord(value) && typeof value.id === "number" && typeof value.email === "string";

const isEventMessage = (value: unknown): value is EventMessage =>
  isObjectRecord(value) &&
  typeof value.id === "number" &&
  typeof value.title === "string" &&
  typeof value.message === "string" &&
  typeof value.createDate === "string";

const normalizeEntityId = (id: string | number) => String(id);

const parseEventsPagination = (
  response: unknown,
  fallbackTotal: number
): EventsPagination | undefined => {
  const payload = getApiPayload(response);
  if (!isObjectRecord(payload) || !isObjectRecord(payload.pagination)) {
    return undefined;
  }

  const { pagination } = payload;

  return {
    page: Number(pagination.page ?? 1),
    limit: Number(pagination.limit ?? (fallbackTotal || 5)),
    total: Number(pagination.total ?? fallbackTotal),
    totalPages: Number(pagination.totalPages ?? 1),
  };
};

const eventTag = (id: string | number) => ({
  type: "events" as const,
  id: normalizeEntityId(id),
});
const eventListTag = { type: "events" as const, id: "LIST" };
const eventSubTag = (id: string | number, suffix: string) => ({
  type: "events" as const,
  id: `${normalizeEntityId(id)}-${suffix}`,
});

const eventInscriptionInvalidationTags = (id: EventUsersIdsFetch["id"]) =>
  [eventSubTag(id, "users"), eventTag(id), eventListTag];

export const eventsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getEvents: build.query<PaginatedEventsResponse, EventsParams>({
      query: ({ featured, eventStatus, sortBy: _sortBy, ...params }) => ({
        url: "/bo/events",
        params: {
          ...params,
          ...(eventStatus ? { eventStatus } : {}),
          ...(featured === true
            ? { featured: "true" }
            : featured === false
              ? { featured: "false" }
              : {}),
        },
      }),
      transformResponse: (response: unknown) => {
        const events = normalizeEventListResponse(response).map((event) => mapEvent(event));
        return {
          events,
          pagination: parseEventsPagination(response, events.length),
        };
      },
      providesTags: (result) =>
        result?.events
          ? [
              ...result.events.map(({ id }) => eventTag(id)),
              eventListTag,
            ]
          : [eventListTag],
    }),
    deleteEvent: build.mutation<Event[], number>({
      query: (id) => ({
        url: `/bo/events/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [eventTag(id), eventListTag],
    }),
deleteEventAndRecurrents: build.mutation<Event[], number>({
      query: (id) => ({
        url: `/bo/events/recurrent/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: (_result, _error, id) => [
        eventTag(id),
        eventListTag,
        eventSubTag(id, "recurrent"),
      ],
    }),
    getEventById: build.query<Event, string | number>({
      query: (id) => ({
        url: `/bo/events/${id}`,
      }),
      transformResponse: (response: unknown) => {
        return mapEvent(normalizeEventResponse(response));
      },
      providesTags: (_result, _error, id) => [eventTag(id)],
    }),
    getRecurrentEvents: build.query<Event[], string | number>({
       query: (id) => ({
         url: `/bo/events/recurrent/${id}`,
         method: "GET",
      }),
      transformResponse: (response: unknown) => {
        const events = normalizeEventListResponse(response);
        return events.map((event) => mapEvent(event));
      },
      providesTags: (_result, _error, id) => [eventSubTag(id, "recurrent")],
    }),
    createEvent: build.mutation<CreateEventResponse, EventFetch>({
      query: (data) => ({
        url: "/bo/events",
        method: "POST",
        body: data,
      }),
      transformResponse: (
        response: ApiDataResponse<CreateEventResponse> | CreateEventResponse
      ) => {
        return unwrapApiData(response);
      },
      invalidatesTags: (result) => [
        eventListTag,
        ...(result?.id != null ? [eventTag(result.id)] : []),
      ],
    }),



createEventAndRecurrents: build
      .mutation<CreateEventAndRecurrentsResponse, createEventAndRecurrentsDTO>({
        query: (data) => ({
          url: "/bo/events/recurrent",
          method: "POST",
          body: data
        }),
        transformResponse: (
          response:
            | ApiDataResponse<CreateEventAndRecurrentsResponse>
            | CreateEventAndRecurrentsResponse
        ) => {
          return unwrapApiData(response);
        },
        invalidatesTags: [eventListTag],
      }),

updateEvent: build.mutation<void, EventFetch>({
      query: (data) => ({
        url: "/bo/events",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, data) => [
        ...(data.id != null ? [eventTag(data.id)] : []),
        eventListTag,
      ],
    }),

    getOccupiedFeaturedOrders: build.query<
      { id: number; order: number; eventName: string }[],
      string
    >({
      query: (tenantId) => ({
        url: "/bo/events/featured/occupied-orders",
        params: { tenantId },
      }),
      transformResponse: (response: unknown) => {
        const payload = unwrapApiData(response as ApiDataResponse<unknown> | unknown);
        return Array.isArray(payload) ? payload : [];
      },
      providesTags: [eventListTag],
    }),

    updateFeaturedEventsOrder: build.mutation<void, { tenantId: string | null, events: { id: number, featuredOrder: number | null }[] }>({
      query: (data) => ({
        url: "/bo/events/featured/order",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [eventListTag],
    }),

updateRecurrentEvents: build.mutation<void, EventFetch>({
      query: (data) => ({
        url: "/bo/events/recurrent",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, data) => [
        ...(data.id != null ? [eventTag(data.id), eventSubTag(data.id, "recurrent")] : []),
        eventListTag,
      ],
    }),

updateEventHost: build.mutation<void, UpdateHostFetch>({
      query: (data) => ({
        url: "/bo/events/host",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, data) =>
        eventInscriptionInvalidationTags(data.id),
    }),
addUsersEvent: build.mutation<void, EventUsersIdsFetch>({
      query: (data) => ({
        url: "/bo/events/addUsers",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) =>
        eventInscriptionInvalidationTags(id),
    }),
getEventUsers: build.query<EventUsers[], number | string>({
      query: (id) => ({
        url: `/bo/events/${id}/users`,
      }),
      transformResponse: (response: unknown) => {
        return getArrayPayload(response, isEventUser);
      },
      providesTags: (_result, _error, id) => [eventSubTag(id, "users")],
    }),
deactivateEvent: build.mutation<void, number | string>({
      query: (id) => ({
        url: `/bo/events/${id}/cancel`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [eventTag(id), eventListTag],
    }),
activateEvent: build.mutation<void, number | string>({
      query: (id) => ({
        url: `/bo/events/${id}/state/activate`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [eventTag(id), eventListTag],
    }),
	mutEventUsers: build.mutation<void, EventUsersIdsFetch>({
	      query: ({ id, usersIds }) => ({
	        url: `/bo/events/${id}/mut`,
        method: "PATCH",
        body: { usersIds },
      }),
      invalidatesTags: (_result, _error, { id }) =>
        eventInscriptionInvalidationTags(id),
    }),
deleteEventUsers: build.mutation<RemoveEventUsersResult, RemoveEventUsersFetch>({
      query: ({ id, usersIds, refund }) => ({
        url: `/bo/events/${id}/users`,
        method: "DELETE",
        body: { usersIds, refund },
      }),
      // 207 cuando el lote fue parcial: la respuesta trae el detalle por
      // usuario, así que se desenvuelve igual que un 200 (GTK-736).
      transformResponse: (response: ApiDataResponse<RemoveEventUsersResult> | RemoveEventUsersResult) =>
        unwrapApiData(response),
      invalidatesTags: (_result, _error, { id }) =>
        eventInscriptionInvalidationTags(id),
    }),
savePayments: build.mutation<void, EventUsersIdsFetch>({
      query: ({ id, usersIds }) => ({
        url: `/bo/events/${id}/users/pay`,
        method: "PATCH",
        body: { usersIds },
      }),
      invalidatesTags: (_result, _error, { id }) =>
        eventInscriptionInvalidationTags(id),
    }),
unsavePayments: build.mutation<void, EventUsersIdsFetch>({
      query: ({ id, usersIds }) => ({
        url: `/bo/events/${id}/users/unpaid`,
        method: "PATCH",
        body: { usersIds },
      }),
      invalidatesTags: (_result, _error, { id }) =>
        eventInscriptionInvalidationTags(id),
    }),
getEventCancellations: build.query<EventCancellationsResponse, number | string>({
      query: (id) => ({
        url: `/bo/events/${id}/cancellations`,
      }),
      transformResponse: (
        response:
          | ApiDataResponse<EventCancellationsResponse>
          | EventCancellationsResponse
      ) => {
        return unwrapApiData(response);
      },
      providesTags: (_result, _error, id) => [eventSubTag(id, "cancellations")],
    }),

    // Event images management
    getEventImages: build.query<EventImage[], number | string>({
      query: (id) => ({
        url: `/bo/events/${id}/images`,
      }),
      transformResponse: (response: unknown) => {
        return getArrayPayload(response, isEventImage);
      },
      providesTags: (_result, _error, id) => [eventSubTag(id, "images")],
    }),

    addEventImages: build.mutation<void, { id: number | string; images: EventImagePayload[] }>({
      query: ({ id, images }) => ({
        url: `/bo/events/${id}/images`,
        method: "POST",
        body: { images },
      }),
      invalidatesTags: (_result, _error, { id }) => [eventSubTag(id, "images")],
    }),

    deleteAllEventImages: build.mutation<void, number | string>({
      query: (id) => ({
        url: `/bo/events/${id}/images`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [eventSubTag(id, "images")],
    }),

    deleteEventImage: build.mutation<void, { eventId: number | string; imageId: number | string }>({
      query: ({ eventId, imageId }) => ({
        url: `/bo/events/${eventId}/images/${imageId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { eventId }) => [eventSubTag(eventId, "images")],
    }),

    // Event state toggle
    toggleEventState: build.mutation<void, number | string>({
      query: (id) => ({
        url: `/bo/events/${id}/state`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, id) => [eventTag(id), eventListTag],
    }),

    getEventInterest: build.query<EventInterestResponse, EventInterestParams>({
      query: ({ id, page, limit, firstName, lastName, email }) => ({
        url: `/bo/events/${id}/interest`,
        params: {
          page,
          limit,
          ...(firstName?.trim() ? { firstName: firstName.trim() } : {}),
          ...(lastName?.trim() ? { lastName: lastName.trim() } : {}),
          ...(email?.trim() ? { email: email.trim() } : {}),
        },
      }),
      transformResponse: (response: unknown): EventInterestResponse => {
        const payload = getApiPayload(response);
        const items = isObjectRecord(payload) && Array.isArray(payload.items)
          ? payload.items.filter(isEventInterestUser)
          : [];
        const rawPagination = isObjectRecord(payload) ? payload.pagination : undefined;

        return {
          items,
          pagination: {
            page: Number(isObjectRecord(rawPagination) ? rawPagination.page : 1) || 1,
            limit: Number(isObjectRecord(rawPagination) ? rawPagination.limit : 25) || 25,
            total: Number(isObjectRecord(rawPagination) ? rawPagination.total : items.length) || 0,
            totalPages: Number(isObjectRecord(rawPagination) ? rawPagination.totalPages : 1) || 1,
          },
        };
      },
      providesTags: (_result, _error, { id }) => [eventSubTag(id, "interest")],
    }),

    // Event messages
    getEventMessages: build.query<EventMessage[], number | string>({
      query: (id) => ({
        url: `/bo/events/${id}/messages`,
      }),
      transformResponse: (response: unknown) => {
        return getArrayPayload(response, isEventMessage);
      },
      providesTags: (_result, _error, id) => [eventSubTag(id, "messages")],
    }),
  }),
});



export const {
  useCreateEventMutation,
  useCreateEventAndRecurrentsMutation,
  useGetEventsQuery,
  useGetRecurrentEventsQuery,
  useDeleteEventMutation,
  useDeleteEventAndRecurrentsMutation,
  useGetEventByIdQuery,
  useUpdateEventMutation,
  useGetOccupiedFeaturedOrdersQuery,
  useUpdateFeaturedEventsOrderMutation,
  useUpdateRecurrentEventsMutation,
  useUpdateEventHostMutation,
  useAddUsersEventMutation,
  useGetEventUsersQuery,
  useMutEventUsersMutation,
  useDeleteEventUsersMutation,
  useDeactivateEventMutation,
  useActivateEventMutation,
  useSavePaymentsMutation,
  useUnsavePaymentsMutation,
  useGetEventCancellationsQuery,
  useGetEventInterestQuery,
  useGetEventImagesQuery,
  useAddEventImagesMutation,
  useDeleteAllEventImagesMutation,
  useDeleteEventImageMutation,
  useToggleEventStateMutation,
  useGetEventMessagesQuery,
} = eventsApi;
